import React, { useEffect, useMemo, useState } from "react";
import styles from "./Payments.module.scss";
import classNames from "classnames/bind";
import { SearchIcon } from "~/assets/icons";
import { ChevronDown, ChevronRight } from "lucide-react";
import BaseSelectCustom from "~/components/common/base-select-custom/BaseSelectCustom";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import stripeApi, { IPaymentHistoryItem } from "~/apis/stripe";
import { notify } from "~/utils/helper";

const cx = classNames.bind(styles);

interface Transaction {
  id: string;
  type: string;
  contactName: string;
  relatedJob: string;
  relatedInvoice: string;
  amountGross: number;
  merchantFees: number;
  customerFees: number;
  amountNet: number;
  status: string;
  referenceId: string;
  jobId?: string;
}

interface Payout {
  id: string;
  payoutDate: string;
  amountGross: number;
  merchantFees: number;
  customerFees: number;
  amountNet: number;
  status: string;
  transactions: Transaction[];
}

const dateRangeOptions = [
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "90", label: "Last 90 days" },
  { id: "365", label: "Last year" },
];

const toAmount = (value: number | null | undefined): number =>
  Number.isFinite(Number(value)) ? Number(value) : 0;

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);

const formatDisplayDate = (date: string): string => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const dateKey = (date: string): string => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toISOString().slice(0, 10);
};

const formatStatusLabel = (status: string | null | undefined): string => {
  if (!status) return "—";
  const normalized = status.toLowerCase();
  if (normalized === "succeeded") return "Processed";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const isSuccessStatus = (status: string): boolean => {
  const normalized = status.toLowerCase();
  return (
    normalized === "processed" ||
    normalized === "succeeded" ||
    normalized === "settled"
  );
};

const isFailedStatus = (status: string): boolean => {
  const normalized = status.toLowerCase();
  return normalized === "failed" || normalized.includes("fail");
};

const aggregateGroupStatus = (transactions: Transaction[]): string => {
  if (transactions.length === 0) return "—";
  if (transactions.some((tx) => isFailedStatus(tx.status))) return "Failed";
  if (transactions.every((tx) => isSuccessStatus(tx.status))) return "Processed";
  if (transactions.some((tx) => tx.status.toLowerCase() === "pending")) {
    return "Pending";
  }
  return "Mixed";
};

const TYPE_LABELS: Record<string, string> = {
  "checkout.session.completed": "Card Payment",
  "checkout.session.created": "Checkout Created",
  "checkout_session_created": "Checkout Created",
  "checkout.session.expired": "Checkout Expired",
  "manual_payment_created": "Manual Payment",
  "payment_intent.succeeded": "Card Payment",
  "payment_intent.payment_failed": "Payment Failed",
  "charge.succeeded": "Card Payment",
  "charge.refunded": "Refund",
};

const formatTypeLabel = (type: string | null | undefined): string => {
  if (!type) return "Payment";
  if (TYPE_LABELS[type]) return TYPE_LABELS[type];
  return type
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getDateRange = (days: string | number) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - Number(days));
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
};

const mapTransaction = (item: IPaymentHistoryItem): Transaction => {
  const amountGross = toAmount(item.amountGross);
  const merchantFees = toAmount(item.merchantFees);
  const customerFees = toAmount(item.customerFees);
  const amountNet =
    item.amountNet != null
      ? toAmount(item.amountNet)
      : amountGross + merchantFees + customerFees;

  return {
    id: String(item.id),
    type: formatTypeLabel(item.type),
    contactName: item.contactName || "—",
    relatedJob: item.relatedJob || "—",
    relatedInvoice: item.relatedInvoice || "—",
    amountGross,
    merchantFees,
    customerFees,
    amountNet,
    status: formatStatusLabel(item.status),
    referenceId: item.referenceId || String(item.id),
    jobId: item.jobId != null ? String(item.jobId) : undefined,
  };
};

const groupPayouts = (items: IPaymentHistoryItem[]): Payout[] => {
  const groups = new Map<string, Payout>();

  for (const item of items) {
    const key = dateKey(item.payoutDate);
    const transaction = mapTransaction(item);
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        id: key,
        payoutDate: formatDisplayDate(item.payoutDate),
        amountGross: transaction.amountGross,
        merchantFees: transaction.merchantFees,
        customerFees: transaction.customerFees,
        amountNet: transaction.amountNet,
        status: aggregateGroupStatus([transaction]),
        transactions: [transaction],
      });
      continue;
    }

    existing.amountGross += transaction.amountGross;
    existing.merchantFees += transaction.merchantFees;
    existing.customerFees += transaction.customerFees;
    existing.amountNet += transaction.amountNet;
    existing.transactions.push(transaction);
    existing.status = aggregateGroupStatus(existing.transactions);
  }

  return Array.from(groups.values()).sort((a, b) => b.id.localeCompare(a.id));
};

const Payments = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<string | number>("30");
  const [expandedPayouts, setExpandedPayouts] = useState<Set<string>>(new Set());
  const [items, setItems] = useState<IPaymentHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPayments = async () => {
      setIsLoading(true);
      try {
        const { startDate, endDate } = getDateRange(dateRange);
        const response = await stripeApi.getPaymentHistories({ startDate, endDate });
        setItems(response.data?.data ?? []);
      } catch (error: any) {
        setItems([]);
        notify(
          "error",
          error?.response?.data?.message || "Failed to load payments"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayments();
  }, [dateRange]);

  const payouts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = !query
      ? items
      : items.filter((item) => {
          const haystack = [
            item.contactName,
            item.relatedJob,
            item.relatedInvoice,
            item.referenceId,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(query);
        });

    return groupPayouts(filtered);
  }, [items, searchQuery]);

  const togglePayout = (payoutId: string) => {
    setExpandedPayouts((prev) => {
      const next = new Set(prev);
      if (next.has(payoutId)) next.delete(payoutId);
      else next.add(payoutId);
      return next;
    });
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case "processed":
      case "succeeded":
      case "settled":
        return cx("status-badge", "status-processed");
      case "pending":
      case "mixed":
        return cx("status-badge", "status-settled");
      case "failed":
        return cx("status-badge");
      default:
        return cx("status-badge");
    }
  };

  return (
    <div className={cx("payments-container")}>
      <div className={cx("header")}>
        <h1 className={cx("title")}>Payments</h1>
        <div className={cx("header-controls")}>
          <div className={cx("search-container")}>
            <SearchIcon className={cx("search-icon")} />
            <input
              type="text"
              placeholder="Search by job and customer name"
              className={cx("search-input")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <BaseSelectCustom
            selectedView={dateRange}
            onViewChange={(viewId) => setDateRange(viewId ?? "30")}
            viewOptions={dateRangeOptions}
            placeholder="Last 30 days"
            className={cx("date-range-selector")}
            minWidth="150px"
            IconDrop={
              <div className={cx("drop-icon")}>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.95906 4.47461L6.69906 7.73461C6.31406 8.11961 5.68406 8.11961 5.29906 7.73461L2.03906 4.47461"
                    stroke="#F9FBFE"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            }
          />
        </div>
      </div>

      <div className={cx("table-container")}>
        <div className={cx("table-header")}>
          <div className={cx("table-header-cell", "col-expand")}></div>
          <div className={cx("table-header-cell", "col-date")}>Payout Date</div>
          <div className={cx("table-header-cell", "col-amount")}>
            Amount (Gross)
          </div>
          <div className={cx("table-header-cell", "col-fees")}>
            Merchant Fees
          </div>
          <div className={cx("table-header-cell", "col-fees")}>
            Customer Fees
          </div>
          <div className={cx("table-header-cell", "col-amount")}>
            Amount (Net)
          </div>
          <div className={cx("table-header-cell", "col-status")}>Status</div>
        </div>

        <div className={cx("table-body")}>
          {isLoading ? (
            <LoadingMainContent />
          ) : payouts.length === 0 ? (
            <div className={cx("empty-state")}>No payments found.</div>
          ) : (
            payouts.map((payout) => {
              const isExpanded = expandedPayouts.has(payout.id);
              return (
                <React.Fragment key={payout.id}>
                  <div
                    className={cx("payout-row")}
                    onClick={() => togglePayout(payout.id)}
                  >
                    <div className={cx("table-cell", "col-expand")}>
                      {isExpanded ? (
                        <ChevronDown className={cx("expand-icon")} />
                      ) : (
                        <ChevronRight className={cx("expand-icon")} />
                      )}
                    </div>
                    <div className={cx("table-cell", "col-date")}>
                      {payout.payoutDate}
                    </div>
                    <div className={cx("table-cell", "col-amount")}>
                      {formatCurrency(payout.amountGross)}
                    </div>
                    <div
                      className={cx(
                        "table-cell",
                        "col-fees",
                        payout.merchantFees < 0 && "negative"
                      )}
                    >
                      {formatCurrency(payout.merchantFees)}
                    </div>
                    <div className={cx("table-cell", "col-fees")}>
                      {formatCurrency(payout.customerFees)}
                    </div>
                    <div className={cx("table-cell", "col-amount")}>
                      {formatCurrency(payout.amountNet)}
                    </div>
                    <div className={cx("table-cell", "col-status")}>
                      <span className={getStatusBadgeClass(payout.status)}>
                        {payout.status}
                      </span>
                    </div>
                  </div>

                  {isExpanded && payout.transactions.length > 0 && (
                    <div className={cx("transactions-container")}>
                      <div className={cx("transactions-header")}>
                        <div className={cx("table-header-cell", "col-type")}>
                          Type
                        </div>
                        <div className={cx("table-header-cell", "col-contact")}>
                          Contact Name
                        </div>
                        <div className={cx("table-header-cell", "col-job")}>
                          Related Job
                        </div>
                        <div className={cx("table-header-cell", "col-invoice")}>
                          Related Invoice
                        </div>
                        <div className={cx("table-header-cell", "col-amount")}>
                          Amount (Gross)
                        </div>
                        <div className={cx("table-header-cell", "col-fees")}>
                          Merchant Fees
                        </div>
                        <div className={cx("table-header-cell", "col-fees")}>
                          Customer Fees
                        </div>
                        <div className={cx("table-header-cell", "col-amount")}>
                          Amount (Net)
                        </div>
                        <div className={cx("table-header-cell", "col-status")}>
                          Status
                        </div>
                        <div className={cx("table-header-cell", "col-reference")}>
                          Reference ID
                        </div>
                      </div>
                      {payout.transactions.map((transaction) => (
                        <div
                          key={transaction.id}
                          className={cx("transaction-row")}
                        >
                          <div className={cx("table-cell", "col-type")}>
                            {transaction.type}
                          </div>
                          <div className={cx("table-cell", "col-contact")}>
                            {transaction.contactName}
                          </div>
                          <div className={cx("table-cell", "col-job")}>
                            {transaction.relatedJob}
                          </div>
                          <div className={cx("table-cell", "col-invoice")}>
                            {transaction.relatedInvoice}
                          </div>
                          <div className={cx("table-cell", "col-amount")}>
                            {formatCurrency(transaction.amountGross)}
                          </div>
                          <div
                            className={cx(
                              "table-cell",
                              "col-fees",
                              transaction.merchantFees < 0 && "negative"
                            )}
                          >
                            {formatCurrency(transaction.merchantFees)}
                          </div>
                          <div className={cx("table-cell", "col-fees")}>
                            {formatCurrency(transaction.customerFees)}
                          </div>
                          <div className={cx("table-cell", "col-amount")}>
                            {formatCurrency(transaction.amountNet)}
                          </div>
                          <div className={cx("table-cell", "col-status")}>
                            <span
                              className={getStatusBadgeClass(transaction.status)}
                            >
                              {transaction.status}
                            </span>
                          </div>
                          <div className={cx("table-cell", "col-reference")}>
                            {transaction.referenceId}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Payments;
